import { useEffect, useRef, useState } from "react";
import { BrowserMultiFormatReader } from "@zxing/browser";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Loader2 } from "lucide-react";

// Kamera-Scanner (QR + Barcode/EAN) mit manueller Eingabe als Fallback.
// onDetected(code) wird bei erkanntem Code ODER bei manueller Eingabe gerufen.
export default function BarcodeScanDialog({ open, onClose, onDetected }) {
    const videoRef = useRef(null);
    const controlsRef = useRef(null);
    const [err, setErr] = useState("");
    const [manual, setManual] = useState("");
    const [starting, setStarting] = useState(false);

    useEffect(() => {
        if (!open) return undefined;
        let active = true;
        setErr("");
        setManual("");
        setStarting(true);
        const reader = new BrowserMultiFormatReader();
        const stop = () => {
            active = false;
            try { controlsRef.current && controlsRef.current.stop(); } catch { /* ignore */ }
        };
        reader.decodeFromConstraints(
            { video: { facingMode: { ideal: "environment" } } },
            videoRef.current,
            (result, _e, controls) => {
                controlsRef.current = controls;
                if (result && active) {
                    const text = result.getText();
                    stop();
                    onDetected(text);
                }
            },
        ).then((controls) => {
            controlsRef.current = controls;
            if (active) setStarting(false);
        }).catch(() => {
            if (active) { setErr("Kamera nicht verfügbar — bitte Artikelnummer/EAN eintippen."); setStarting(false); }
        });
        return () => stop();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const submitManual = () => {
        const c = manual.trim();
        if (c) onDetected(c);
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="sm:max-w-[420px]" data-testid="barcode-scan-dialog">
                <DialogHeader><DialogTitle>Teil scannen</DialogTitle></DialogHeader>
                <div className="space-y-3">
                    <div className="relative rounded-sm overflow-hidden bg-black aspect-[4/3]">
                        <video ref={videoRef} className="w-full h-full object-cover" muted playsInline />
                        {starting && (
                            <div className="absolute inset-0 flex items-center justify-center text-white text-xs">
                                <Loader2 className="w-5 h-5 animate-spin mr-2" /> Kamera startet…
                            </div>
                        )}
                    </div>
                    {err && <p className="text-xs text-amber-700" data-testid="barcode-scan-error">{err}</p>}
                    <div>
                        <label className="text-[10px] uppercase tracking-wider font-bold text-gray-500">Oder Artikelnummer / EAN eintippen</label>
                        <div className="flex gap-2 mt-1">
                            <Input value={manual} onChange={(e) => setManual(e.target.value)}
                                onKeyDown={(e) => e.key === "Enter" && submitManual()}
                                placeholder="z. B. 4012345678901" className="rounded-sm font-mono-num"
                                data-testid="barcode-manual-input" autoFocus />
                            <Button type="button" onClick={submitManual} disabled={!manual.trim()} className="rounded-sm" data-testid="barcode-manual-submit">Übernehmen</Button>
                        </div>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
